/** @typedef {import("overline").Deferred} Deferred */

import zipWith from "lodash/zipWith";
import { Sentry } from "#src/glitchtip.js";
import { downloadURL } from "downloaders";
import {
  drawingsToDXM,
  createDrawingSet,
} from "#src/extensions/converters/dxm";
import itemTagGenerator from "@local/extensions/utilities/item-tag.js";
import {
  VANDELAY_URL,
  CORVELAY_PUBLIC_URL,
  CORVELAY_PRIVATE_URL,
  DEFAULT_LOG_LEVEL,
} from "#src/env";
import { api } from "#src/api";
import { getLogger } from "@diagraphics/logging";
import { count, identity } from "ramda";

const log = getLogger(Symbol("download-job-bundle"), DEFAULT_LOG_LEVEL, true);

export async function createDXF(group, selectedLocations) {
  const drawings = drawingsToDXM(group, selectedLocations);
  return await createDrawingSet(drawings);
}

function archiveFilenameResolver(bucket, prefix) {
  /** @type {(x?: any) => string} */
  let resolveDirname = () => bucket;

  if (typeof prefix === "function") {
    resolveDirname = prefix;
  } else if (typeof prefix === "string") {
    resolveDirname = () => prefix;
  }

  return function resolveArchiveFilename(item) {
    const basename = item.path.split("/").pop();
    const dirname = resolveDirname(item.path);
    return `${dirname}/${basename}`;
  };
}

async function createSignedURLs(bucket, paths, prefix) {
  let members = {};

  if (paths.length > 0) {
    const { data, error } = await api.storage
      .from(bucket)
      .createSignedUrls(paths, 120);

    if (error || data["error"]) {
      log.warn(
        `[download-job-bundle] Error obtaining signed URLs for bucket "${bucket}"`,
      );
    } else {
      log.debug(`[download-job-bundle] Signed urls: %o`, data);

      /* This is hack to account for a discrepancy in return values between dev and production
       * At the time of writing, in production we get an array of objects with a "path" and
       * "signedUrl" property, while in development we only get a "signedUrl" property.
       *
       * Therefore, we check for the existence of the "path" property, and if it is not present
       * then we zip the signedUrls with the paths we provided in the request. Let's hope that
       * the API always returns the URLs in the same order as the provided paths!
       *
       * For further details see:
       *
       * https://github.com/supabase/storage-js/issues/169
       * https://github.com/supabase/storage-js/issues/169
       * https://diagraphics.slack.com/archives/C030GPLTNNR/p1690500911464139
       *
       * TODO: remove this hack once the API and client code stablize
       *
       */

      const items = data.every((item) => "path" in item)
        ? data
        : zipWith(paths, data, (path, { signedUrl }) => ({ path, signedUrl }));

      const resolveArchiveFilename = archiveFilenameResolver(bucket, prefix);

      members = items.reduce((acc, item) => {
        const filename = resolveArchiveFilename(item);
        acc[filename] = item.signedUrl;
        return acc;
      }, {});
    }
  }

  return members;
}

/*
async function getDataURLFromDownload(bucket, path) {
  const { data, error } = await api.storage.from(bucket).download(path);
  if (error) {
    return null;
  } else {
    return await getDataURL(data);
  }
}

async function createDataURLs(bucket, paths, prefix) {

  const resolveArchiveFilename = archiveFilenameResolver(bucket, prefix);

  if (paths.length > 0) {
    const pending = paths.map((path) => getDataURLFromDownload(bucket, path));
    const completed = await Promise.all(pending);
    const data = zip(paths, completed);

    return data.reduce((acc, [path, url]) => {
      if (url) {
        const filename = resolveArchiveFilename(path);
        acc[filename] = url;
        return acc;
      }
    }, {});
  }
  return {};
}
*/

function createPublicURLs(bucket, paths, prefix) {
  const resolveArchiveFilename = archiveFilenameResolver(bucket, prefix);

  return paths.reduce((members, path) => {
    const {
      data: { publicUrl },
    } = api.storage.from(bucket).getPublicUrl(path);
    const filename = resolveArchiveFilename({ path });
    members[filename] = publicUrl;
    return members;
  }, {});
}

async function createDrawingFiles(group) {
  const { items } = group;

  /* TODO: generating the itemTagGenerator here means that duplicate marks will
     get mangled even if there are no duplicates when items are restricted to
     those with width and height set. This leads to "unnecessary" name
     mangling, but it ensures that item names will be consistent between this function
     and others e.g. createAttachments */
  const itemTag = itemTagGenerator(items);
  const pathMap = new Map();

  for (const id of items.order) {
    const { drawing } = items[id];
    if (drawing) {
      const path = `${drawing.id}/${drawing.name}`;
      const tag = itemTag(id);
      pathMap.set(path, tag);
    }
  }

  const paths = [...pathMap.keys()];
  const prefixer = (path) => `drawings/${pathMap.get(path)}`;

  return await createPublicURLs("drawings", paths, prefixer);
}

async function createAttachments(group) {
  const { attachments } = group;

  if (attachments) {
    const paths = attachments.map((a) => a.object_id);

    return await createSignedURLs("attachments", paths);
  }
}

async function createSummary(
  group,
  filename,
  single = false,
  selectedLocations,
  itemsOnly = false,
  collectionsOnly = false,
) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const jobId = group.job.id;
  let url = `${prefix}/jobs/${jobId}/summary.xlsx`;
  if (selectedLocations || itemsOnly || collectionsOnly) {
    const body = {};
    if (selectedLocations) {
      body.locations = selectedLocations;
    }

    if (itemsOnly) {
      body.items_only = true;
    } else if (collectionsOnly) {
      body.collections_only = true;
    }

    return {
      [filename]: {
        url: `${prefix}/jobs/${jobId}/loc-summary.xlsx`,
        type: "POST",
        body: JSON.stringify(body),
      },
    };
  }

  return {
    [filename]: url,
  };
}

async function createReportXls(
  group,
  filename,
  single = false,
  reportId = null,
) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const jobId = group.job.id;
  const url = reportId
    ? `${prefix}/jobs/${jobId}/report.xlsx/${reportId}`
    : `${prefix}/jobs/${jobId}/report.xlsx`;

  return {
    [filename]: url,
  };
}

async function createReportJson(
  group,
  filename,
  single = false,
  reportId = null,
  version,
  platform,
) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const jobId = group.job.id;

  return {
    [filename]: {
      url: `${prefix}/jobs/${jobId}/report-openings.json`,
      type: "POST",
      body: JSON.stringify({
        reportId,
        origin: window.location.origin,
        version,
        platform,
      }),
    },
  };
}

async function createOpenings(
  group,
  filename,
  single = false,
  version,
  platform,
  selectedLocations,
) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const jobId = group.job.id;

  if (selectedLocations?.length) {
    return {
      [filename]: {
        url: `${prefix}/jobs/${jobId}/loc-openings.json`,
        type: "POST",
        body: JSON.stringify({
          locations: selectedLocations,
          origin: window.location.origin,
          version,
          platform,
        }),
      },
    };
  } else {
    return {
      [filename]: {
        url: `${prefix}/jobs/${jobId}/loc-openings.json`,
        type: "POST",
        body: JSON.stringify({
          origin: window.location.origin,
          version,
          platform,
        }),
      },
    };
  }
}

async function createBOM(group, filename, single = false) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const jobId = group.job.id;
  const url = `${prefix}/jobs/${jobId}/bom.xlsx`;

  return {
    [filename]: url,
  };
}

async function createProductQuotePDF(jobId, filename, single = false) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const url = `${prefix}/jobs/${jobId}/product-quote.pdf`;

  return {
    [filename]: url,
  };
}

async function createPDF(
  group,
  _,
  __,
  filename,
  single = false,
  reportId = null,
) {
  const prefix = single ? CORVELAY_PUBLIC_URL : CORVELAY_PRIVATE_URL;
  const jobId = group.job.id;
  const url = reportId
    ? `${prefix}/jobs/${jobId}/summary.pdf/${reportId}`
    : `${prefix}/jobs/${jobId}/summary.pdf`;

  return {
    [filename]: url,
  };
}

/**
 *
 * @param {Blob} blob
 * @returns {Promise<string | ArrayBuffer>}
 */
async function getDataURL(blob) {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.addEventListener("loadend", () => resolve(reader.result));
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(blob);
  });
}

export async function postArchive(archive) {
  const zipResponse = await fetch(`${VANDELAY_URL}/zip/archives`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(archive),
  });

  if (zipResponse.status == 201) {
    const location = zipResponse.headers.get("location");

    return `${VANDELAY_URL}${location}`;
  }

  return null;
}

/**
 *
 * @param {*} group
 * @param {*} types
 * @param {*} options
 */
export async function downloadJobBundle(
  group,
  types,
  productData,
  options,
  profile,
) {
  const {
    filename = "job",
    productQuote,
    includeProductQuotePdf = false,
    includeReportsExcel = [],
    includeReportsWBP = [],
    includeReportsGS = [],
    includeReportsPdf = [],
    selectedLocations = null,
    includeData = false,
    includeCAD = false,
    includeBOM = false,
    includeAttachments = false,
    includeWBP = false,
    includeGS = false,
    itemsOnly = false,
    collectionsOnly = false,
    wbpVersion,
    gsVersion,
    collectOnly = false, // return the merged members map instead of downloading
    forArchive = false, // force archive-ready members (never the single-file public-URL path)
  } = options;

  const numRequests = count(identity, [
    includeData,
    includeCAD,
    includeAttachments,
    includeBOM,
    includeWBP,
    includeGS,
    ...includeReportsExcel.filter((r) => r),
    ...includeReportsWBP.filter((r) => r),
    ...includeReportsGS.filter((r) => r),
    ...includeReportsPdf.filter((r) => r),
  ]);

  // `single` drives the create* builders' "one file, download it directly"
  // behaviour. forArchive forces the multi-file (archive-ready) member shape
  // even for a one-format bucket, so a collectOnly caller always gets a
  // members map it can fold into a larger zip.
  const single = forArchive ? false : numRequests === 1;

  let requests = [];

  if (includeProductQuotePdf && productQuote) {
    requests.push(
      createProductQuotePDF(group.job.id, `${filename}.pdf`, single),
    );
  }

  includeReportsPdf.forEach((r, index) => {
    const reportId = group.item_reports.order[index];
    const report = group.item_reports[reportId];
    if (report && r) {
      const name = report.report_name || "Report";
      requests.push(
        createPDF(group, types, productData, `${name}.pdf`, single, report.id),
      );
    }
  });

  includeReportsExcel.forEach((r, index) => {
    const reportId = group.item_reports.order[index];
    const report = group.item_reports[reportId];
    if (report && r) {
      const name = report.report_name || "Report";
      const req = createReportXls(group, `${name}.xlsx`, single, report.id);
      requests.push(req);
    }
  });

  includeReportsWBP.forEach((r, index) => {
    const reportId = group.item_reports.order[index];
    const report = group.item_reports[reportId];
    if (report && r) {
      const name = report.report_name || "Report";
      const req = createReportJson(
        group,
        `${name} - wbp.json`,
        single,
        report.id,
        wbpVersion,
        "wbp",
      );
      requests.push(req);
    }
  });

  includeReportsGS.forEach((r, index) => {
    const reportId = group.item_reports.order[index];
    const report = group.item_reports[reportId];
    if (report && r) {
      const name = report.report_name || "Report";
      const req = createReportJson(
        group,
        `${name} - gs.json`,
        single,
        report.id,
        gsVersion,
        "gs",
      );
      requests.push(req);
    }
  });

  if (includeData) {
    const name = `${filename} - Summary.xlsx`;
    const req = createSummary(
      group,
      name,
      single,
      selectedLocations,
      itemsOnly,
      collectionsOnly,
    );
    requests.push(req);
  }
  if (includeCAD) {
    requests.push(createDXF(group, selectedLocations));
  }

  if (includeBOM) {
    requests.push(createBOM(group, `${filename} - BOM.xlsx`, single));
  }

  if (includeAttachments) {
    requests.push(createDrawingFiles(group));
    requests.push(createAttachments(group));
    /* Fabrications */
  }

  if (includeWBP) {
    const name = `${filename} - wbp.json`;
    const req = createOpenings(
      group,
      name,
      single,
      wbpVersion,
      "wbp",
      selectedLocations,
    );
    requests.push(req);
  }

  if (includeGS) {
    const name = `${filename} - gs.json`;
    const req = createOpenings(
      group,
      name,
      single,
      gsVersion,
      "gs",
      selectedLocations,
    );
    requests.push(req);
  }

  if (requests.length === 0) {
    return;
  }

  const responses = await Promise.all(requests);

  // Collect mode: hand back the merged members map for the caller to namespace
  // and zip, instead of downloading here. Earlier responses win on a key
  // collision, matching the archive branch below.
  const merged = responses.reduce((acc, m) => ({ ...m, ...acc }), {});
  if (collectOnly) return merged;

  const [responseHead] = responses;
  const [fileHead, ...fileTail] = Object.keys(responseHead);

  /* Only create a zip file if there is more than one file */
  if (numRequests === 1 && !includeCAD && fileTail.length === 0) {
    const content = responseHead[fileHead];
    if (typeof content === "string") {
      downloadURL(content, fileHead);
    } else if (typeof content === "object") {
      const result = await fetch(content.url, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: content.body,
      });

      if (result.ok) {
        const blob = await result.blob();
        const url = URL.createObjectURL(blob);
        downloadURL(url, fileHead);
      } else {
        console.log("Error fetching file", result);
        Sentry.captureException(
          new Error(`Error fetching file for download: ${result.status}`),
        );
      }
    }
  } else if (numRequests > 0) {
    const members = responses.reduce((acc, members) => {
      acc = { ...members, ...acc };
      return acc;
    }, {});

    const downloadTarget = await postArchive({
      filename,
      members,
    });

    if (downloadTarget) {
      downloadURL(downloadTarget, filename);
    }
  }
}

// Zip a pre-built members map (e.g. collected across scope/page buckets by a
// collectOnly caller) into one archive and trigger a single download.
export async function downloadArchive(filename, members) {
  const target = await postArchive({ filename, members });
  if (target) downloadURL(target, filename);
  return Boolean(target);
}
